import { BEIGE_500, BEIGE_700, GRAY_200, GRAY_600, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Check } from '@mui/icons-material'

import Box from '@mui/material/Box'
import { FC } from 'react'
import { MessageAction } from 'models/conversations'
import { OrderPickerList } from './OrderPickerList'
import Stack from '@mui/material/Stack'
import { StatusPill } from './StatusPill'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

const ORDER_LIST_THRESHOLD = 5

interface Props {
  actions: MessageAction[]
  /** Posts the chosen action back into the thread. */
  onSelect: (action: MessageAction) => void
  /**
   * The chosen action's value. Fully controlled on purpose: the parent clears it when the send is rolled
   * back, which is what unlocks the picker again.
   */
  selectedValue?: string
  /** Locks the picker without marking anything selected — a read-only thread still shows its history. */
  locked?: boolean
}

/**
 * In-thread suggestion picker rendered under an assistant bubble. Every non-`reply` action (order,
 * property, visual, personal-info, workspace) becomes a selectable card, and five or more orders one
 * compact list; `reply` actions become quiet pill chips. Selecting one locks the whole group and greys
 * out the alternatives so a turn can only be answered once.
 */
export const MessageSuggestions: FC<Props> = ({ actions, onSelect, selectedValue, locked = false }) => {
  const { t } = useTranslation(['chat'])

  const selected = selectedValue ?? null
  const isLocked = locked || selected != null

  const handleSelect = (action: MessageAction) => {
    if (isLocked) return
    onSelect(action)
  }

  // The payload is backend-shaped but LLM-authored: a non-array would throw here, and the nearest
  // boundary is the application root.
  const safeActions = Array.isArray(actions) ? actions.filter((action) => !!action?.value) : []
  const cardActions = safeActions.filter((action) => action.kind !== 'reply')
  const replyActions = safeActions.filter((action) => action.kind === 'reply')
  const orderActions = cardActions.filter((action) => action.kind === 'order')
  const listsOrders = orderActions.length >= ORDER_LIST_THRESHOLD
  const cards = listsOrders ? cardActions.filter((action) => action.kind !== 'order') : cardActions

  return (
    <Stack gap="0.8rem" role="group" aria-label={t('chat:suggestions.aria_label')} sx={{ marginTop: '0.4rem' }}>

      {listsOrders &&
        <OrderPickerList actions={orderActions} selected={selected} isLocked={isLocked} onSelect={handleSelect} />
      }

      {cards.map((action, index) => {
        const isSelected = selected === action.value
        const isDisabled = isLocked && !isSelected

        return (
          <Stack
            key={`${action.value}-${index}`}
            gap="0.8rem"
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-disabled={isLocked}
            aria-pressed={isSelected}
            onClick={() => handleSelect(action)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              handleSelect(action)
            }}
            sx={{
              padding: '1.4rem 1.6rem',
              borderRadius: '1.2rem',
              border: `1px solid ${isSelected ? BEIGE_700 : GRAY_200}`,
              backgroundColor: isSelected ? BEIGE_500 : WHITE,
              cursor: isDisabled ? 'default' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': isDisabled || isSelected ? undefined : { borderColor: BEIGE_700, backgroundColor: GRAY_200 },
            }}
          >

            <Stack direction="row" alignItems="flex-start" gap="1.2rem">

              {!!action.thumbnail &&
                <Box
                  component="img"
                  src={action.thumbnail}
                  alt=""
                  referrerPolicy="no-referrer"
                  sx={{ width: '4.4rem', height: '4.4rem', flexShrink: 0, borderRadius: '0.8rem', objectFit: 'cover' }}
                />
              }

              <Stack gap="0.2rem" sx={{ flex: 1, minWidth: 0 }}>

                <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
                  {action.label}
                </Typography>

                {!!action.sublabel &&
                  <Typography variant="text-sm" color={GRAY_600} noWrap>
                    {action.sublabel}
                  </Typography>
                }

              </Stack>

              {!!action.status && <StatusPill status={action.status} />}

              {isSelected &&
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  title={t('chat:suggestions.selected')}
                  sx={{ flexShrink: 0, width: '2rem', height: '2rem', borderRadius: '999px', backgroundColor: BEIGE_700, color: GRAY_900 }}
                >
                  <Check sx={{ fontSize: '1.4rem' }} />
                </Stack>
              }

            </Stack>

            {Array.isArray(action.tags) && action.tags.length > 0 &&
              <Stack direction="row" flexWrap="wrap" gap="0.6rem">
                {action.tags.map((tag) => (
                  <Typography
                    key={tag}
                    variant="text-sm"
                    color={GRAY_600}
                    sx={{ padding: '0.2rem 0.9rem', borderRadius: '0.8rem', border: `1px solid ${GRAY_200}`, backgroundColor: GRAY_200 }}
                  >
                    {tag}
                  </Typography>
                ))}
              </Stack>
            }

          </Stack>
        )
      })}

      {replyActions.length > 0 &&
        <Stack direction="row" flexWrap="wrap" gap="0.6rem">
          {replyActions.map((action, index) => {
            const isSelected = selected === action.value
            const isDisabled = isLocked && !isSelected

            return (
              <Typography
                key={`${action.value}-${index}`}
                variant="text-sm"
                fontWeight={600}
                color={isSelected ? GRAY_900 : GRAY_700}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isLocked}
                aria-pressed={isSelected}
                onClick={() => handleSelect(action)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  handleSelect(action)
                }}
                sx={{
                  padding: '0.6rem 1.4rem',
                  borderRadius: '999px',
                  border: `1px solid ${isSelected ? BEIGE_700 : GRAY_200}`,
                  backgroundColor: isSelected ? BEIGE_500 : WHITE,
                  cursor: isDisabled ? 'default' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  '&:hover': isDisabled || isSelected ? undefined : { borderColor: BEIGE_700, backgroundColor: GRAY_200 },
                }}
              >
                {action.label}
              </Typography>
            )
          })}
        </Stack>
      }

    </Stack>
  )
}
